import React, { useState, useEffect, useCallback } from "react";
import { Trophy, Loader2, Target, Ban, CircleDot, RefreshCw } from "lucide-react";
import { paperList } from "../lib/api";

const Stat = ({ label, value, tone, sub, active, onClick }) => (
  <button type="button" onClick={onClick} disabled={!onClick}
    className={`bg-[#0a1020] border rounded-xl px-3 py-2 text-center transition-colors ${active ? "border-[#FFD700]" : "border-[#ffffff12]"} ${onClick ? "hover:border-[#FFD70080] cursor-pointer" : "cursor-default"}`}
    data-testid={`paper-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <p className="text-cyan-300/50 text-[10px] uppercase tracking-wider">{label}</p>
    <p className={`font-bold text-lg ${tone || "text-cyan-50"}`}>{value}</p>
    {sub != null && <p className={`text-[11px] ${tone || "text-cyan-200/60"}`}>{sub}</p>}
  </button>
);

const fmtUsd = (v) => (v == null ? "—" : `${v > 0 ? "+" : v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)}`);
const FILTERS = {
  all: () => true,
  open: (x) => x.status === "open",
  closed: (x) => x.status === "closed",
  wins: (x) => x.status === "closed" && x.result === "win",
  losses: (x) => x.status === "closed" && x.result !== "win",
};
const FILTER_LABEL = { all: "All trades", open: "Open", closed: "Closed", wins: "Wins", losses: "Losses" };

const pnlColor = (p) => (p > 0 ? "text-emerald-300" : p < 0 ? "text-rose-300" : "text-cyan-200/70");
const fmtPnl = (p) => (p == null ? "—" : `${p > 0 ? "+" : ""}${p}%`);

export default function NexusPaper({ session }) {
  const userId = session.user_id;
  const [data, setData] = useState({ items: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const pick = (f) => setFilter((cur) => (cur === f ? "all" : f));

  const load = useCallback(async () => {
    try { setData(await paperList(userId)); } catch (e) {} finally { setLoading(false); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);


  const s = data.stats || {};
  const items = data.items || [];
  const shown = items.filter(FILTERS[filter]);
  const shownUsd = shown.reduce((a, x) => a + (x.pnl_usd || 0), 0);

  const Row = ({ p }) => {
    const long = (p.direction || "").toLowerCase() === "long";
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0a1020] border border-[#ffffff0d] text-sm" data-testid="paper-row">
        {p.status === "closed"
          ? (p.result === "win" ? <Target className="w-4 h-4 text-emerald-300 shrink-0" /> : <Ban className="w-4 h-4 text-rose-300 shrink-0" />)
          : <CircleDot className="w-4 h-4 text-[#FFD700] shrink-0" />}
        <span className="font-mono text-cyan-50 font-semibold text-xs sm:text-sm break-all min-w-0 max-w-[40%]">{p.symbol}</span>
        <span className={`text-[11px] ${long ? "text-emerald-300/80" : "text-rose-300/80"}`}>{p.direction}</span>
        <span className="text-cyan-300/40 text-[10px] uppercase hidden sm:inline">{p.market}</span>
        <div className="ml-auto text-right min-w-0" data-testid="paper-row-money">
          <p className="text-cyan-100 text-xs">
            ${Number(p.notional || 0).toFixed(Number(p.notional || 0) < 10 ? 2 : 0)}{" "}
            <span className="text-[9px] uppercase px-1 rounded bg-emerald-500/15 text-emerald-300">{p.venue}</span>
          </p>
          <p className="text-cyan-300/50 text-[10px]">{p.entry ?? "—"} → {(p.status === "closed" ? p.close_price : p.current) ?? "—"}</p>
        </div>
        <div className="w-20 text-right shrink-0">
          <p className={`text-xs font-semibold ${pnlColor(p.pnl_usd)}`}>{fmtUsd(p.pnl_usd)}</p>
          <p className={`text-[10px] ${pnlColor(p.pnl_pct)}`}>{fmtPnl(p.pnl_pct)}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full overflow-y-auto bg-[#030712] p-4 sm:p-6" data-testid="paper-panel">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-[#FFD700] font-bold text-2xl flex items-center gap-2"><Trophy className="w-6 h-6" /> Track Record</h2>
            <p className="text-cyan-200/60 text-sm mt-1">Real trades only — every position the House actually placed on Coinbase and Kalshi, with real dollars.</p>
          </div>
          <button onClick={load} data-testid="paper-refresh" className="text-cyan-300/50 hover:text-cyan-100 p-2"><RefreshCw className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          <Stat label="Win Rate" value={`${s.win_rate ?? 0}%`} tone="text-[#FFD700]" active={filter === "closed"} onClick={() => pick("closed")} />
          <Stat label="Closed" value={s.closed ?? 0} sub={fmtUsd(s.closed_pnl_usd)} active={filter === "closed"} onClick={() => pick("closed")} />
          <Stat label="Wins" value={s.wins ?? 0} tone="text-emerald-300" active={filter === "wins"} onClick={() => pick("wins")} />
          <Stat label="Losses" value={s.losses ?? 0} tone="text-rose-300" active={filter === "losses"} onClick={() => pick("losses")} />
          <Stat label="Open" value={s.open ?? 0} tone="text-[#FFD700]" sub={fmtUsd(s.open_pnl_usd)} active={filter === "open"} onClick={() => pick("open")} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Stat label="Avg Win" value={fmtPnl(s.avg_win)} tone={pnlColor(s.avg_win)} active={filter === "wins"} onClick={() => pick("wins")} />
          <Stat label="Open P&L" value={fmtPnl(s.open_pnl)} tone={pnlColor(s.open_pnl)} sub={fmtUsd(s.open_pnl_usd)} active={filter === "open"} onClick={() => pick("open")} />
        </div>
        <p className="text-cyan-200/40 text-[11px]" data-testid="paper-note">Tap a card to filter. ${Number(s.deployed || 0).toFixed(2)} currently deployed in open positions.</p>

        {loading ? (
          <div className="text-center py-10 text-cyan-300/40"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-cyan-200/40" data-testid="paper-empty">
            <Trophy className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No real trades yet. Live orders placed by the House show up here.</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <p className="text-cyan-300/60 text-[11px] uppercase tracking-wider font-bold" data-testid="paper-filter-label">
                {FILTER_LABEL[filter]} ({shown.length}) · <span className={pnlColor(shownUsd)}>{fmtUsd(Math.round(shownUsd * 100) / 100)}</span>
              </p>
              {filter !== "all" && <button onClick={() => setFilter("all")} data-testid="paper-filter-clear" className="text-cyan-300/50 hover:text-cyan-100 text-[11px]">show all</button>}
            </div>
            {shown.length === 0
              ? <p className="text-cyan-200/40 text-sm py-6 text-center" data-testid="paper-filter-empty">No {FILTER_LABEL[filter].toLowerCase()} yet.</p>
              : shown.map((p) => <Row key={p.id} p={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
